import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import TextInputWithLabel from '../TextInput/TextInputWithLabel';
import { Icons, Row } from '../../base/BootstrapComponents/BootstrapComponents';
import './AddressInput.scss';
import { buildAddress, responseLogWithNotification } from '../../../utils/utils';
import FieldDTOWithCol from '../Abstract/FieldDTOWithCol';
import TextInput from '../TextInput/TextInput';
import { normalizeAddress } from '../../../modules/System/actions';
import { countries } from './country';
import FilteredSelectInput from '../SelectInput/FilteredSelectInput';

export function AddressHiddenInput(props) {
  const {
    field,
    fieldState,
    formState,
    init,
    handleChangeJson,
  } = props;
  const { onChange, value, ...fieldData } = field;
  useEffect(() => {
    if (init) {
      init(value);
    }
    if (handleChangeJson) {
      handleChangeJson.current = onChange;
    }
  }, []);
  return (
    (
      <TextInput
        type="hidden"
        fieldState={fieldState}
        formState={formState}
        inputProps={{
          ...fieldData, value: field.value, onChange: () => {},
        }}
        disabled
      />
    )
  );
}

export default function AddressInput(props) {
  const {
    control,
    inputProps,
    label,
  } = props;
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const handleChange = useRef();
  const handleChangeJson = useRef();
  const handleChangeJsonString = useRef();
  const [viewForm, setViewForm] = useState(false);
  const [errorText, setErrorText] = useState('');
  const {
    control: control2, watch, setValue, setError,
  } = useForm({
    defaultValues: {},
  });

  const init = json => {
    if (!json) return;
    let address = {};
    try {
      address = JSON.parse(json);
    } catch (e) {
      address = {};
    }
    setData(address);
  };

  const setData = address => {
    const keys = [
      'country',
      'index',
      'region',
      'area',
      'place',
      'location',
      'street',
      'house',
      'letter',
      'slash',
      'corpus',
      'building',
      'room',
    ];
    if (keys?.length > 0) {
      for (const key of keys) {
        setValue(key, address[key] || '');
      }
    }
  };

  const handleNormalize = () => {
    if (!inputProps?.value?.length) return;
    setErrorText('');
    dispatch(normalizeAddress(inputProps?.value || ''))
      .then(data => {
        const address = data?.address || {};
        if (address && !['GOOD', 'ON_DEMAND', 'POSTAL_BOX'].includes(address['quality-code'])) {
          setErrorText(formatMessage({ id: address['quality-code'] }));
        }
        if (address && !['CONFIRMED_MANUALLY', 'VALIDATED', 'OVERRIDDEN'].includes(address['validation-code'])) {
          // setErrorText(formatMessage({ id: address['validation-code'] }));
        }

        if (address && Object.keys(address).length > 0) {
          address.country = 'Российская Федерация';
        }
        if (typeof handleChange?.current === 'function') {
          handleChange.current(buildAddress(data?.address || {}));
        }
        if (typeof handleChangeJson?.current === 'function') {
          handleChangeJson.current(data?.address || {});
        }
        if (typeof handleChangeJsonString?.current === 'function') {
          handleChangeJsonString.current(JSON.stringify(data?.address || {}));
        }
        setData(address);
      })
      .catch(responseLogWithNotification);
  };

  useEffect(() => {
    const subscription = watch(values => {
      if (typeof handleChange?.current === 'function') {
        handleChange.current(buildAddress(values || {}));
      }
      if (typeof handleChangeJson?.current === 'function') {
        handleChangeJson.current(values || {});
      }
      if (typeof handleChangeJsonString?.current === 'function') {
        handleChangeJsonString.current(JSON.stringify(values || {}));
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  return (
    <div className="address-input">
      <TextInputWithLabel {...props} />
      <div className="normalize-block">
        <div className="normalize-address-row" onClick={() => setViewForm(!viewForm)}>
          <Controller
            control={control}
            name={`${props?.inputProps?.name}_normal`}
            render={({
              field,
              fieldState,
              formState,
            }) => {
              const { onChange, value, ...fieldData } = field;
              handleChange.current = onChange;
              return (
                (
                  <TextInputWithLabel
                    fieldState={{ ...fieldState, error: errorText || fieldState.error }}
                    formState={formState}
                    label={label + formatMessage({ id: 'normalized.address' })}
                    inputProps={{
                      ...fieldData, value: field.value, onChange: () => {},
                    }}
                    disabled
                    variant="material"
                  />
                )
              );
            }}
          />
          <Controller
            control={control}
            name={`${props?.inputProps?.name}_json`}
            render={props => <AddressHiddenInput handleChangeJson={handleChangeJson} {...props} />}
          />
          <Controller
            control={control}
            name={`${props?.inputProps?.name}_json_string`}
            render={props => <AddressHiddenInput handleChangeJson={handleChangeJsonString} init={init} {...props} />}
          />
        </div>
        <div className="normalize-reload-data" onClick={handleNormalize}>
          <Icons icon="update" customIcon />
        </div>
      </div>
      <div className={classNames('normalize-address-fields', { show: viewForm })}>
        <Row className="row-cols-2">
          <FieldDTOWithCol
            control={control2}
            component={FilteredSelectInput}
            dtoObject={{ name: 'country', labelKey: 'normalize.country' }}
            variant="material"
            options={countries.map(c => ({ value: c, name: c }))}
            insertDefault
            withLabel
          />
          <FieldDTOWithCol
            control={control2}
            component={TextInputWithLabel}
            dtoObject={{ name: 'index', labelKey: 'normalize.index' }}
            variant="material"
          />

          <FieldDTOWithCol
            control={control2}
            component={TextInputWithLabel}
            dtoObject={{ name: 'region', labelKey: 'normalize.region' }}
            variant="material"
          />

          <FieldDTOWithCol
            control={control2}
            component={TextInputWithLabel}
            dtoObject={{ name: 'area', labelKey: 'normalize.area' }}
            variant="material"
          />

          <FieldDTOWithCol
            control={control2}
            component={TextInputWithLabel}
            dtoObject={{ name: 'place', labelKey: 'normalize.place' }}
            variant="material"
          />

          <FieldDTOWithCol
            control={control2}
            component={TextInputWithLabel}
            dtoObject={{ name: 'location', labelKey: 'normalize.location' }}
            variant="material"
          />

          <FieldDTOWithCol
            control={control2}
            component={TextInputWithLabel}
            dtoObject={{ name: 'street', labelKey: 'normalize.street' }}
            variant="material"
          />

          <FieldDTOWithCol
            control={control2}
            component={TextInputWithLabel}
            dtoObject={{ name: 'house', labelKey: 'normalize.house' }}
            variant="material"
          />

          <FieldDTOWithCol
            control={control2}
            component={TextInputWithLabel}
            dtoObject={{ name: 'vladenie', labelKey: 'normalize.vladenie' }}
            variant="material"
          />

          <FieldDTOWithCol
            control={control2}
            component={TextInputWithLabel}
            dtoObject={{ name: 'letter', labelKey: 'normalize.letter' }}
            variant="material"
          />

          <FieldDTOWithCol
            control={control2}
            component={TextInputWithLabel}
            dtoObject={{ name: 'slash', labelKey: 'normalize.slash' }}
            variant="material"
          />

          <FieldDTOWithCol
            control={control2}
            component={TextInputWithLabel}
            dtoObject={{ name: 'corpus', labelKey: 'normalize.corpus' }}
            variant="material"
          />

          <FieldDTOWithCol
            control={control2}
            component={TextInputWithLabel}
            dtoObject={{ name: 'building', labelKey: 'normalize.building' }}
            variant="material"
          />

          <FieldDTOWithCol
            control={control2}
            component={TextInputWithLabel}
            dtoObject={{ name: 'room', labelKey: 'normalize.room' }}
            variant="material"
          />
        </Row>
      </div>
    </div>
  );
}
